import { useMemo, useState, useEffect } from 'react';

/**
 * Hook para obtener el plan de cuotas y el historial de pagos de lotes.
 * - Recibe un lote individual o un array de lotes
 * - Devuelve: { cuotasData, loading, error }
 */
export function useCuotasLote(lotes: any | any[]) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Normalizar input: convertir lote individual a array
  const lotesArray = useMemo(() => {
    return Array.isArray(lotes) ? lotes : [lotes];
  }, [lotes]);

  // Procesar cada lote
  const cuotasData = useMemo(() => {
    try {
      setLoading(true);
      setError(null);

      console.log('Entrando a map de useCuotasLote', lotesArray);
      const resultado = lotesArray.map((lote: any) => {
        if (!lote) {
          console.log('Lote nulo o indefinido:', lote);
          return null;
        }

        // Historial de pagos (abonos y enganche)
        const pagosHistorial = (Array.isArray(lote.payments) ? lote.payments : [])
          .filter((p: any) => p.type === 'abono' || p.type === 'enganche')
          .map((p: any) => ({
            fecha: p.date,
            tipo: p.type,
            monto: p.amount,
            recibo: p.receipt || p.receiptNumber || '-',
            estado: p.type === 'abono' ? 'Pagado' : 'Enganche',
          }))
          .sort((a: any, b: any) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

        // Cálculo de cuotas
        const numPagos = lote.numeroPagos || lote.paymentCount || lote.numberOfPayments || lote.numPayments || 0;
        const totalValue = lote.totalValue || parseFloat(lote.valorTotal) || 0;
        const enganche = (Array.isArray(lote.payments) ? lote.payments : []).find((p: any) => p.type === 'enganche')?.amount || 0;
        const saldo = totalValue - enganche;
        const cuotaMensual = numPagos > 0 ? saldo / numPagos : 0;
        const fechaVenta = new Date(lote.saleDate || lote.fechaVenta || lote.fechaInicio || new Date());
        const payments = Array.isArray(lote.payments) ? lote.payments : [];
        const cuotasArr: any[] = [];

        // Función helper para añadir meses de forma segura
        const addMonthsSafe = (date: Date, months: number): Date => {
          const result = new Date(date);
          const originalDay = result.getDate();
          const targetMonth = result.getMonth() + months;
          const targetYear = result.getFullYear() + Math.floor(targetMonth / 12);
          const finalMonth = ((targetMonth % 12) + 12) % 12;
          
          result.setFullYear(targetYear);
          result.setMonth(finalMonth);
          
          const lastDayOfTargetMonth = new Date(targetYear, finalMonth + 1, 0).getDate();
          result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
          
          return result;
        };

        for (let i = 1; i <= numPagos; i++) {
          const fechaVencimiento = addMonthsSafe(fechaVenta, i);
          const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];
          // Buscar si existe un abono para esta cuota
          const pago = payments.find((p: any) =>
            p.type === 'abono' && String(p.cuotaNumero) === String(i) && (p.cuotaFechaVencimiento === fechaVencimientoYMD || p.date === fechaVencimientoYMD)
          );
          const pagada = !!pago;
          const hoy = new Date();
          const vencida = !pagada && fechaVencimiento < hoy;
          cuotasArr.push({
            cuotaNumero: i,
            fecha: fechaVencimientoYMD,
            monto: cuotaMensual,
            pagada,
            vencida,
            proxima: !pagada && !vencida && cuotasArr.findIndex((c: any) => !c.pagada && !c.vencida) === -1,
            pago
          });
        }

        const totalPagado = cuotasArr.filter((c: any) => c.pagada).reduce((sum: number, c: any) => sum + (parseFloat(c.monto) || 0), 0);
        const saldoPendiente = cuotasArr.filter((c: any) => !c.pagada).reduce((sum: number, c: any) => sum + (parseFloat(c.monto) || 0), 0);

        console.log('Procesando lote', lote.id || lote.numero, {
          numPagos, totalValue, payments, cuotasArr, totalPagado, saldoPendiente, pagosHistorial
        });

        const result = {
          numero: lote.numero || lote.id || lote.lote || '',
          cuotas: cuotasArr,
          totalPagado,
          saldoPendiente,
          numeroPagos: numPagos,
          montoCuota: cuotaMensual,
          pagosHistorial
        };
        console.log('Objeto devuelto para lote', lote.id || lote.numero, result);
        return result;
      });
      console.log('Resultado final de map en useCuotasLote', resultado);
      return resultado;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      console.error('Error en useCuotasLote:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [lotesArray]);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [lotesArray]);

  return {
    cuotasData,
    loading,
    error
  };
}

/**
 * Hook para obtener el plan de cuotas y el historial de pagos de un lote individual.
 * - Recibe el objeto lote (con payments, numeroPagos, etc)
 * - Devuelve: { cuotas, totalPagado, saldoPendiente, numeroPagos, montoCuota, pagosHistorial }
 */
export function useCuotasLoteIndividual(lote: any) {
  // Historial de pagos (abonos y enganche)
  const pagosHistorial = useMemo(() => {
    if (!lote || !lote.payments) return [];
    return lote.payments
      .filter((p: any) => p.type === 'abono' || p.type === 'enganche')
      .map((p: any) => ({
        fecha: p.date,
        tipo: p.type,
        monto: p.amount,
        recibo: p.receipt || p.receiptNumber || '-',
        estado: p.type === 'abono' ? 'Pagado' : 'Enganche',
      }))
      .sort((a: any, b: any) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  }, [lote]);

  // Cálculo de cuotas
  const {
    cuotas,
    totalPagado,
    saldoPendiente,
    numeroPagos,
    montoCuota
  } = useMemo(() => {
    if (!lote) return { cuotas: [], totalPagado: 0, saldoPendiente: 0, numeroPagos: 0, montoCuota: 0 };
    const numPagos = lote.numeroPagos || lote.paymentCount || lote.numberOfPayments || 0;
    const totalValue = lote.totalValue || 0;
    const enganche = (lote.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
    const saldo = totalValue - enganche;
    const cuotaMensual = numPagos > 0 ? saldo / numPagos : 0;
    const fechaVenta = new Date(lote.saleDate || lote.fechaInicio || new Date());
    const payments = lote.payments || [];
    const cuotasArr: any[] = [];
    
    // Función helper para añadir meses de forma segura
    const addMonthsSafe = (date: Date, months: number): Date => {
      const result = new Date(date);
      const originalDay = result.getDate();
      const targetMonth = result.getMonth() + months;
      const targetYear = result.getFullYear() + Math.floor(targetMonth / 12);
      const finalMonth = ((targetMonth % 12) + 12) % 12;
      
      result.setFullYear(targetYear);
      result.setMonth(finalMonth);
      
      const lastDayOfTargetMonth = new Date(targetYear, finalMonth + 1, 0).getDate();
      result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
      
      return result;
    };
    
    for (let i = 1; i <= numPagos; i++) {
      const fechaVencimiento = addMonthsSafe(fechaVenta, i);
      const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];
      // Buscar si existe un abono para esta cuota
      const pago = payments.find((p: any) =>
        p.type === 'abono' && String(p.cuotaNumero) === String(i) && (p.cuotaFechaVencimiento === fechaVencimientoYMD || p.date === fechaVencimientoYMD)
      );
      const pagada = !!pago;
      const hoy = new Date();
      const vencida = !pagada && fechaVencimiento < hoy;
      cuotasArr.push({
        cuotaNumero: i,
        fecha: fechaVencimientoYMD,
        monto: cuotaMensual,
        pagada,
        vencida,
        proxima: !pagada && !vencida && cuotasArr.findIndex((c: any) => !c.pagada && !c.vencida) === -1,
        pago
      });
    }
    const totalPagado = cuotasArr.filter((c: any) => c.pagada).reduce((sum: number, c: any) => sum + (parseFloat(c.monto) || 0), 0);
    const saldoPendiente = cuotasArr.filter((c: any) => !c.pagada).reduce((sum: number, c: any) => sum + (parseFloat(c.monto) || 0), 0);
    return {
      cuotas: cuotasArr,
      totalPagado,
      saldoPendiente,
      numeroPagos: numPagos,
      montoCuota: cuotaMensual
    };
  }, [lote]);

  return {
    cuotas,
    totalPagado,
    saldoPendiente,
    numeroPagos,
    montoCuota,
    pagosHistorial
  };
} 